import { Component } from '@verndale/core';
import { isFocusable } from '../helpers';

class Module extends Component {
  setupDefaults() {
    this.videoId = this.el.dataset.videoId;
    this.service = this.el.dataset.videoService;
    this.accountId = window?.ue?.brightcoveAccountId;
    this.ignoreUtilFocusChanges = false;
    this.lastFocus = null;

    this.handleVideoModalClose = this.handleVideoModalClose.bind(this);
    this.handleEscape = this.handleEscape.bind(this);
    this.handleFocus = this.handleFocus.bind(this);

    this.dom = {
      $body: document._$('body'),
      $link: this.el._$('.video-modal__trigger')
    };
  }

  addListeners() {
    this.el._on('click', this.handleVideoModalOpen.bind(this));
    this.dom.$link._on('keypress', this.handleModalOpenKeyboard.bind(this));
  }

  removeListeners() {
    this.dom.$videoModal.removeEventListener(
      'click',
      this.handleVideoModalClose
    );
    document.removeEventListener('keyup', this.handleEscape);
    document.removeEventListener('focus', this.handleFocus, true);
  }

  buildHtml() {
    let iframeUrl = `https://player.vimeo.com/video/${this.videoId}?autoplay=1`;

    this.dom.$videoModal = document.createElement('div');
    this.dom.$videoModal.classList.add('video-modal');
    this.dom.$videoModal.setAttribute('role', 'dialog');
    this.dom.$videoModal.setAttribute('aria-modal', 'true');

    const privateArray = this.videoId?.split('/');

    if (privateArray && privateArray.length > 1) {
      iframeUrl = `https://player.vimeo.com/video/${privateArray[0]}?h=${privateArray[1]}&autoplay=1`;
    }

    if (this.service === 'vimeo') {
      this.dom.$videoModal.innerHTML = `
        <div class="video-modal__inner">
          <div class="video-modal__inner-height">
            <div class="video-modal__video-wrapper">
            <iframe
              src="${iframeUrl}"
              width="100%"
              height="100%"
              frameborder="0"
              allow="fullscreen"
              webkitallowfullscreen
              mozallowfullscreen
              allowfullscreen>
            </iframe>
            </div>
          </div>
        </div>
        <button aria-label="close button" class="video-modal__close-btn">
          <svg width="20" height="20" viewBox="0 0 10 10">
            <use xlink:href="#close" />
          </svg>
        <button>
      `;
    } else if (this.service === 'brightcove') {
      this.dom.$videoModal.innerHTML = `
        <div class="video-modal__inner">
          <div class="video-modal__inner-height">
            <div class="video-modal__video-wrapper">
              <video data-video-id="${this.videoId}"
                data-account="${this.accountId}"
                data-player="default"
                data-embed="default"
                data-application-id
                autoplay
                class="video-js"
                controls></video>
            </div>
          </div>
        </div>
        <button aria-label="close button" class="video-modal__close-btn">
          <svg width="20" height="20" viewBox="0 0 10 10">
            <use xlink:href="#close" />
          </svg>
        <button>
      `;
    }

    this.dom.$videoWrapper = this.dom.$videoModal.querySelector(
      '.video-modal__video-wrapper'
    );
    this.dom.$videoModal._on('click', this.handleVideoModalClose);
    document._on('keyup', this.handleEscape);
    document._on('focus', this.handleFocus, true);
  }

  handleVideoModalOpen() {
    this.buildHtml();

    this.dom.$body.classList.add('has-modal');
    this.dom.$body.appendChild(this.dom.$videoModal);
    this.focusFirstDescendant(this.dom.$videoModal);

    if (this.videoId && this.accountId) {
      const script = document.createElement('script');
      script.setAttribute(
        'src',
        `//players.brightcove.net/${this.accountId}/default_default/index.min.js`
      );
      script.setAttribute('type', 'text/javascript');
      this.dom.$videoModal.appendChild(script);
    }
  }

  handleModalOpenKeyboard(e) {
    const key = e.which || e.keyCode;

    if (key === 13 || key === 32) {
      e.preventDefault();
      this.handleVideoModalOpen();
    }
  }

  handleVideoModalClose(e) {
    if (
      e.target === this.dom.$videoWrapper ||
      this.dom.$videoWrapper.contains(e.target)
    ) {
      return;
    }
    this.removeListeners();

    this.dom.$body.classList.remove('has-modal');
    this.dom.$body.removeChild(this.dom.$videoModal);
    this.dom.$link.focus();
  }

  handleEscape(e) {
    const key = e.which || e.keyCode;

    if (key === 27) {
      e.stopPropagation();
      this.removeListeners();
      this.dom.$body.classList.remove('has-modal');
      this.dom.$body.removeChild(this.dom.$videoModal);
      this.el.querySelector('.video-modal__trigger').focus();
    }
  }

  handleFocus(e) {
    if (this.ignoreUtilFocusChanges) return;

    if (this.dom.$videoModal.contains(e.target)) {
      this.lastFocus = e.target;
    } else {
      this.focusFirstDescendant(this.dom.$videoModal);

      if (this.lastFocus === document.activeElement) {
        this.focusLastDescendant(this.dom.$videoModal);
      }
      this.lastFocus = document.activeElement;
    }
  }

  focusFirstDescendant(element) {
    for(let i = 0; i < element.childNodes.length; i++) {
      const child = element.childNodes[i];
      if (this.attemptFocus(child) || this.focusFirstDescendant(child)) {
        return true;
      }
    }
    return false;
  }

  focusLastDescendant(element) {
    for(let i = element.childNodes.length - 1; i >= 0; i--) {
      const child = element.childNodes[i];
      if (this.attemptFocus(child) || this.focusLastDescendant(child)) {
        return true;
      }
    }
    return false;
  }

  attemptFocus(element) {
    if (
      !isFocusable(element) ||
      element.classList.contains('video-modal__close-btn')
    ) {
      return false;
    }

    this.ignoreUtilFocusChanges = true;
    try {
      element.focus();
    } catch (e) {
      return;
    }
    this.ignoreUtilFocusChanges = false;
    return document.activeElement === element;
  }
}

export default Module;
